import { useEffect } from 'react';

const SITE_URL = 'https://www.xed21.com';
const SITE_NAME = 'Xed21';

interface PageMetaProps {
  title: string;
  description: string;
  canonicalPath: string;
  keywords?: string[];
  type?: 'website' | 'article';
  imagePath?: string;
  jsonLd?: Record<string, unknown>;
}

function absoluteUrl(path: string) {
  if (path.startsWith('http')) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

export default function PageMeta({
  title,
  description,
  canonicalPath,
  keywords = [],
  type = 'website',
  imagePath = '/Gemini_Generated_Image_1y5sae1y5sae1y5s.jpg',
  jsonLd,
}: PageMetaProps) {
  useEffect(() => {
    const pageTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalUrl = absoluteUrl(canonicalPath);
    const imageUrl = absoluteUrl(imagePath);

    document.title = pageTitle;

    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    if (keywords.length > 0) {
      upsertMeta('meta[name="keywords"]', { name: 'keywords', content: keywords.join(', ') });
    }
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: pageTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: imageUrl });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: pageTitle });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: imageUrl });

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.xed21PageMeta = 'true';
    script.text = JSON.stringify(
      jsonLd || {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: pageTitle,
        description,
        url: canonicalUrl,
      }
    );
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [canonicalPath, description, imagePath, jsonLd, keywords, title, type]);

  return null;
}
